import chalk from 'chalk';
import ora from 'ora';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface KanbanItem {
  id: string;
  title: string;
  description?: string;
  priority: number;
  status: string;
  estimated_minutes?: number;
  notes?: string;
  deliverable?: string;
  result?: string;
  completed_at?: string;
  created_at: string;
}

async function resolveAgentId(codeName: string): Promise<string | null> {
  try {
    const data = await api.get<{ agent_id: string }>(`/agents/${codeName}`);
    return data.agent_id;
  } catch {
    return null;
  }
}

function priorityLabel(p: number): string {
  return p === 1 ? chalk.red('HIGH') : p === 3 ? chalk.dim('LOW') : chalk.yellow('MED');
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    in_progress: chalk.blue('In Progress'),
    todo: chalk.green('To Do'),
    backlog: chalk.dim('Backlog'),
    done: chalk.gray('Done'),
  };
  return map[s] ?? s;
}

// ---------------------------------------------------------------------------
// agt kanban list
// ---------------------------------------------------------------------------

export async function kanbanListCommand(
  opts: { agent: string },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  const spinner = ora({ text: 'Fetching kanban board…', isSilent: json });
  spinner.start();

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    spinner.fail(`Agent "${opts.agent}" not found.`);
    process.exitCode = 1;
    return;
  }

  try {
    const data = await api.post<{ items: KanbanItem[] }>('/host/my-kanban', {
      agent_id: agentId,
    });

    spinner.stop();

    if (json) {
      jsonOutput({ ok: true, items: data.items });
      return;
    }

    if (!data.items.length) {
      info(`Board for ${opts.agent} is empty.`);
      return;
    }

    const rows = data.items.map((item) => [
      priorityLabel(item.priority),
      statusLabel(item.status),
      item.title.length > 50 ? item.title.slice(0, 47) + '…' : item.title,
      item.estimated_minutes ? `~${item.estimated_minutes}min` : '',
      item.id.slice(0, 8),
    ]);

    console.log(chalk.bold(`\nKanban: ${opts.agent}\n`));
    table(['Pri', 'Status', 'Title', 'Est', 'ID'], rows);
  } catch (err) {
    spinner.fail('Failed to fetch board.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt kanban add
// ---------------------------------------------------------------------------

export async function kanbanAddCommand(
  title: string,
  opts: {
    agent: string;
    priority?: string;
    status?: string;
    description?: string;
    estimate?: string;
    deliverable?: string;
  },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    error(`Agent "${opts.agent}" not found.`);
    process.exitCode = 1;
    return;
  }

  try {
    const data = await api.post<{ ok: boolean; added: number }>('/host/kanban', {
      agent_id: agentId,
      add: [
        {
          title,
          description: opts.description,
          priority: opts.priority ? Number(opts.priority) : 2,
          status: opts.status ?? 'todo',
          estimated_minutes: opts.estimate ? Number(opts.estimate) : undefined,
          deliverable: opts.deliverable,
          source: 'manual',
          // ENG-10583: a card added from a terminal answers no conversation, so it
          // must not inherit whichever thread the agent last received.
          no_channel_origin: true,
        },
      ],
    });

    if (json) {
      jsonOutput({ ok: true, added: data.added });
    } else {
      success(`Added "${title}" to ${opts.agent}'s board.`);
    }
  } catch (err) {
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt kanban move
// ---------------------------------------------------------------------------

export async function kanbanMoveCommand(
  titleOrId: string,
  status: string,
  opts: { agent: string; notes?: string },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  const validStatuses = ['backlog', 'todo', 'in_progress', 'done'];
  if (!validStatuses.includes(status)) {
    error(`Invalid status "${status}". Must be one of: ${validStatuses.join(', ')}`);
    process.exitCode = 1;
    return;
  }

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    error(`Agent "${opts.agent}" not found.`);
    process.exitCode = 1;
    return;
  }

  const isUuid = /^[0-9a-f]{8}-/.test(titleOrId);
  const update: Record<string, unknown> = {
    status,
    notes: opts.notes,
  };
  if (isUuid) {
    update.id = titleOrId;
  } else {
    update.title = titleOrId;
  }

  try {
    const data = await api.post<{ ok: boolean; updated: number }>('/host/kanban', {
      agent_id: agentId,
      update: [update],
    });

    if (json) {
      jsonOutput({ ok: true, updated: data.updated });
    } else if (data.updated > 0) {
      success(`Moved "${titleOrId}" → ${status}.`);
    } else {
      error(`Item "${titleOrId}" not found.`);
      process.exitCode = 1;
    }
  } catch (err) {
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt kanban update
// ---------------------------------------------------------------------------

export async function kanbanUpdateCommand(
  titleOrId: string,
  opts: { agent: string; notes?: string; result?: string },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    error(`Agent "${opts.agent}" not found.`);
    process.exitCode = 1;
    return;
  }

  // Fetch current item to get its status
  let board: { items: KanbanItem[] };
  try {
    board = await api.post<{ items: KanbanItem[] }>('/host/my-kanban', {
      agent_id: agentId,
    });
  } catch (err) {
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
    return;
  }

  const isUuid = /^[0-9a-f]{8}-/.test(titleOrId);
  const match = board.items.find(
    (i) => (isUuid && i.id === titleOrId) || i.title === titleOrId,
  );

  if (!match) {
    if (json) {
      jsonOutput({ ok: false, error: `Item "${titleOrId}" not found.` });
    } else {
      error(`Item "${titleOrId}" not found.`);
    }
    process.exitCode = 1;
    return;
  }

  try {
    // CS-1582: no status — this is a notes/result update, and echoing the
    // card's current status back made the API treat it as a move. On a
    // `needs_attention` card (reaper-only, not agent-writable) that echo 400'd
    // as `Invalid status 'needs_attention'`: the card's own status rejected as
    // invalid. Same fix as the MCP `kanban_update` tool.
    const data = await api.post<{ ok: boolean; updated: number }>('/host/kanban', {
      agent_id: agentId,
      update: [
        {
          id: match.id,
          notes: opts.notes,
          result: opts.result,
        },
      ],
    });

    if (json) {
      jsonOutput({ ok: true, updated: data.updated });
    } else {
      success(`Updated "${match.title}".`);
    }
  } catch (err) {
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt kanban done
// ---------------------------------------------------------------------------

export async function kanbanDoneCommand(
  titleOrId: string,
  opts: { agent: string; result?: string; notes?: string },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    error(`Agent "${opts.agent}" not found.`);
    process.exitCode = 1;
    return;
  }

  const isUuid = /^[0-9a-f]{8}-/.test(titleOrId);
  const update: Record<string, unknown> = {
    status: 'done',
    result: opts.result,
    notes: opts.notes,
  };
  if (isUuid) {
    update.id = titleOrId;
  } else {
    update.title = titleOrId;
  }

  try {
    const data = await api.post<{ ok: boolean; updated: number }>('/host/kanban', {
      agent_id: agentId,
      update: [update],
    });

    if (json) {
      jsonOutput({ ok: true, updated: data.updated });
    } else if (data.updated > 0) {
      success(`Marked "${titleOrId}" as done.`);
    } else {
      error(`Item "${titleOrId}" not found.`);
      process.exitCode = 1;
    }
  } catch (err) {
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}
